/**
 * oneGameUrl — D44 (2026-10-09): one game, one URL on helix2000.com.
 *
 * DJ: "we should just have one game one url our play like we talked about
 * before." On the main game domain (environment.ts MAIN_GAME_HOSTNAMES — the
 * same test the clean `/play` address uses) a URL a player types or follows
 * never picks what boots:
 *
 *  - `/play` is the address. A reload of it restores its own boot URL
 *    (mainGamePlayUrl.ts); a fresh `/play` lands on the roster (`/resume`),
 *    which resumes the hero's saved map, or starts the story for a new hero or
 *    a guest (D27).
 *  - `/` (the title) and `/resume` (the roster) stay the game's own pages, with
 *    only the parameters sign-in and the reset wipe need (ENTRY_PARAMS); any
 *    other parameter is dropped.
 *  - Every other path (`/adventure`, `/pve`, `/sunken`, `/intro`, `/brawl`,
 *    `/arena`, `/abilitygrounds`, `/lobby`, `/gmrace`, `/debug`, `/editor`,
 *    `/diagnostic`, `/pretest`, …) and any `?map=`, mode or QA parameter goes
 *    to `/play`, for everyone: GMs use the in-game GM tools.
 *
 * The game's own page loads (the roster booting a hero, a portal, the reset
 * wipe) are honoured: they come through navigateInGame (inGameNavigation.ts).
 * Server routes (`/api/*`, `/health`, the Colyseus socket, `/assets/*`) never
 * load this bundle, so they are untouched. Dev, PTR, QA tunnels and localhost
 * are not the main game domain and keep every URL and parameter. The server's
 * production map gate (src/server/auth/productionMapAccess.ts) stays the
 * backstop.
 */
import { MAIN_GAME_PLAY_PATH } from './mainGamePlayUrl';

/** The roster: a fresh `/play` lands here (mainGamePlayUrl.ts). */
export const ROSTER_PATH = '/resume';
/** The title: Log out lands here, and it goes on to the roster. */
export const TITLE_PATH = '/';

/**
 * Parameters the title and roster may carry: the Twitch sign-in result
 * (twitch.routes.ts computePostOAuthRedirect) and the reset wipe and splash
 * skips applyMainGameRedirect has always kept.
 */
export const ENTRY_PARAMS: ReadonlySet<string> = new Set([
  'twitch',
  'reason',
  'reset',
  'skipSplash',
  'skipLogin',
]);

export type OneGameUrlDecision =
  /** Boot this URL as it is. */
  | { kind: 'keep' }
  /** Drop the foreign parameters in place (no reload). */
  | { kind: 'strip'; url: string }
  /** Leave for `/play`. */
  | { kind: 'play' };

/**
 * What the main game domain does with a page load. `trusted` is true for the
 * game's own navigations and their reloads (inGameNavigation.ts) and for a
 * restored `/play`.
 */
export function decideOneGameUrl(pathname: string, search: string, trusted: boolean): OneGameUrlDecision {
  if (trusted || pathname === MAIN_GAME_PLAY_PATH) return { kind: 'keep' };
  const params = new URLSearchParams(search);
  const foreign = [...params.keys()].filter(key => !ENTRY_PARAMS.has(key));
  if (pathname === ROSTER_PATH) {
    if (foreign.length === 0) return { kind: 'keep' };
    for (const key of foreign) params.delete(key);
    const q = params.toString();
    return { kind: 'strip', url: `${ROSTER_PATH}${q ? `?${q}` : ''}` };
  }
  if (pathname === TITLE_PATH && foreign.length === 0) return { kind: 'keep' };
  return { kind: 'play' };
}
